import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { customersApi, arApi, customerPaymentsApi } from '../services/api';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Building2, Phone, Plus, X, Loader2, Send,
  CheckCircle2, XCircle, Trash2, FileText, MapPin, Landmark, Users, MessageSquare,
  Pencil, Lock, Download, Upload, Link2, Unlink,
  Wallet, Calendar, AlertTriangle, ChevronRight, ChevronDown, ChevronUp, IndianRupee,
  Receipt,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import CustomerAdjustments from '../components/CustomerAdjustments';
import ActiveOrdersCard from '../components/ActiveOrdersCard';
import BulkReminderDialog from '../components/BulkReminderDialog';
import CustomerActivityLog from '../components/CustomerActivityLog';
import EditPaymentDialog from '../components/EditPaymentDialog';

const KYC_LABELS = { pending: 'Pending', in_progress: 'In Progress', complete: 'Submitted', approved: 'Approved', changes_requested: 'Changes Required' };
const KYC_COLORS = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
  complete: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
  changes_requested: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
};

const STEP_META = [
  { num: 1, label: 'Company', icon: Building2 },
  { num: 2, label: 'Address', icon: MapPin },
  { num: 3, label: 'Documents', icon: FileText },
  { num: 4, label: 'Banking', icon: Landmark },
  { num: 5, label: 'Contacts', icon: Users },
];

const ORG_TYPES = ["Proprietorship", "LLP", "Partnership", "Private Limited", "Public Limited", "HUF", "Trust", "Other"];

// Field schema per KYC step. Used by the edit dialog to render the right inputs.
const KYC_STEP_FIELDS = {
  1: [
    { key: 'company_name', label: 'Company Name' },
    { key: 'legal_name', label: 'Legal Name' },
    { key: 'org_type', label: 'Organization Type', type: 'select', options: ORG_TYPES },
    { key: 'mobile', label: 'Mobile (WhatsApp)' },
    { key: 'email', label: 'Email', type: 'email', span: 2 },
  ],
  2: [
    { key: 'billing_address_1', label: 'Billing Address Line 1', span: 2 },
    { key: 'billing_address_2', label: 'Billing Address Line 2', span: 2 },
    { key: 'billing_city', label: 'Billing City' },
    { key: 'billing_state', label: 'Billing State' },
    { key: 'billing_pin', label: 'Billing PIN' },
    { key: 'shipping_same_as_billing', label: 'Shipping same as billing', type: 'checkbox', span: 2 },
    { key: 'shipping_address_1', label: 'Shipping Address Line 1', span: 2, hideIf: (f) => f.shipping_same_as_billing },
    { key: 'shipping_address_2', label: 'Shipping Address Line 2', span: 2, hideIf: (f) => f.shipping_same_as_billing },
    { key: 'shipping_city', label: 'Shipping City', hideIf: (f) => f.shipping_same_as_billing },
    { key: 'shipping_state', label: 'Shipping State', hideIf: (f) => f.shipping_same_as_billing },
    { key: 'shipping_pin', label: 'Shipping PIN', hideIf: (f) => f.shipping_same_as_billing },
  ],
  3: [
    { key: 'gst_number', label: 'GST Number', upper: true, placeholder: '22AAAAA0000A1Z5' },
    { key: 'pan_number', label: 'PAN Number', upper: true, placeholder: 'AAAAA0000A' },
    { key: 'msme_number', label: 'MSME Registration', span: 2 },
  ],
  4: [
    { key: 'account_holder', label: 'Account Holder Name', span: 2 },
    { key: 'account_number', label: 'Account Number' },
    { key: 'account_type', label: 'Account Type', type: 'select', options: ['Current', 'Savings', 'OD'] },
    { key: 'ifsc', label: 'IFSC Code', upper: true },
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'branch_name', label: 'Branch Name' },
    { key: 'branch_address', label: 'Branch Address', span: 2 },
  ],
  5: [
    { key: 'ops_name', label: 'Ops Contact Name' },
    { key: 'ops_phone', label: 'Ops WhatsApp' },
    { key: 'ops_email', label: 'Ops Email', type: 'email', span: 2 },
    { key: 'finance_name', label: 'Finance Contact Name' },
    { key: 'finance_phone', label: 'Finance WhatsApp' },
    { key: 'finance_email', label: 'Finance Email', type: 'email', span: 2 },
  ],
};

const STEP_TITLE = { 1: 'Company Details', 2: 'Addresses', 3: 'Documents', 4: 'Banking Details', 5: 'Contact Persons' };

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Which detail tab is active. URL-synced so a link like
  // /customers/:id?tab=receivables lands on the right view.
  const validTabs = ['orders', 'activity', 'receivables', 'ledger', 'adjustments'];
  const tabFromUrl = searchParams.get('tab');
  // Backwards-compat: 'adjustments' was renamed to 'ledger'
  const normalizedTab = tabFromUrl === 'adjustments' ? 'ledger' : tabFromUrl;
  const activeTab = validTabs.includes(normalizedTab) ? normalizedTab : 'orders';
  const setActiveTab = useCallback((next) => {
    const nsp = new URLSearchParams(searchParams);
    nsp.set('tab', next);
    setSearchParams(nsp, { replace: true });
    // Bring the tab bar to the top of the viewport for immediate visibility.
    requestAnimationFrame(() => {
      const el = document.getElementById('customer-detail-tabs');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [searchParams, setSearchParams]);
  const [cust, setCust] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kycActionType, setKycActionType] = useState(null);
  const [kycMessage, setKycMessage] = useState('');
  const [kycActioning, setKycActioning] = useState(false);
  const [phoneDialog, setPhoneDialog] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newPhoneLabel, setNewPhoneLabel] = useState('');
  const [newPhoneName, setNewPhoneName] = useState('');
  const [newPhoneDesignation, setNewPhoneDesignation] = useState('other');
  const [newPhoneEmail, setNewPhoneEmail] = useState('');
  const [newPhoneShared, setNewPhoneShared] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [replyTexts, setReplyTexts] = useState({});
  // Edit profile dialog
  const [profileDialog, setProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({ company_name: '', mobile: '', email: '', contact_person_name: '', contact_designation: 'operations' });
  const [savingProfile, setSavingProfile] = useState(false);
  // Send login link state — per-contact, fires DoubleTick
  const [sendingLinkPhone, setSendingLinkPhone] = useState(null);
  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  // Linked companies
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [linkDialog, setLinkDialog] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [linking, setLinking] = useState(null);
  const docFileRefs = {
    pricing_terms: useRef(null),
    msme_certificate: useRef(null),
    gst_certificate: useRef(null),
    other: useRef(null),
  };
  const [otherLabel, setOtherLabel] = useState('');

  const fetchDocuments = async () => {
    try {
      const r = await customersApi.listDocuments(customerId);
      setDocuments(r.data);
    } catch {}
  };

  const fetchLinkedCompanies = async () => {
    try { const r = await customersApi.listLinks(customerId); setLinkedCompanies(r.data || []); }
    catch {}
  };

  const openLinkDialog = async () => {
    try {
      const r = await customersApi.getAll();
      setAllCustomers(r.data);
      setLinkSearch('');
      setLinkDialog(true);
    } catch (e) { toast.error('Could not load customer list'); }
  };

  const handleLink = async (targetId) => {
    setLinking(targetId);
    try {
      await customersApi.link(customerId, targetId);
      toast.success('Linked');
      setLinkDialog(false);
      fetchLinkedCompanies();
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
    finally { setLinking(null); }
  };

  const handleUnlink = async (targetId) => {
    try {
      await customersApi.unlink(customerId, targetId);
      toast.success('Unlinked');
      fetchLinkedCompanies();
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Unlink failed'); }
  };

  const handleUploadDocument = async (category, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    setUploadingCategory(category);
    try {
      const label = category === 'other' ? (otherLabel || file.name.replace(/\.pdf$/i, '')) : '';
      await customersApi.uploadDocument(customerId, file, category, label);
      toast.success('Document uploaded');
      setOtherLabel('');
      fetchDocuments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCategory(null);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await customersApi.deleteDocument(customerId, docId);
      toast.success('Document deleted');
      fetchDocuments();
    } catch { toast.error('Failed'); }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const r = await customersApi.downloadDocument(customerId, doc.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleSendLoginLink = async (phone) => {
    if (!phone) return;
    setSendingLinkPhone(phone);
    try {
      const r = await customersApi.sendLoginLink(customerId, phone);
      if (r.data?.whatsapp_sent) {
        toast.success(`Login link sent to ${phone} on WhatsApp`);
      } else {
        toast.warning(`Link generated but WhatsApp delivery failed. Please check DoubleTick template config.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send login link');
    } finally {
      setSendingLinkPhone(null);
    }
  };
  // Edit phone dialog (for additional phones)
  const [editPhoneDialog, setEditPhoneDialog] = useState(null); // { oldPhone, phone, label }
  // KYC step edit dialog
  const [kycEditDialog, setKycEditDialog] = useState(null); // { step, form }
  const [savingKycEdit, setSavingKycEdit] = useState(false);

  const fetchCustomer = async () => {
    try { const res = await customersApi.get(customerId); setCust(res.data); }
    catch { toast.error('Customer not found'); navigate('/customers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomer(); fetchDocuments(); fetchLinkedCompanies(); }, [customerId]);

  const handleKycAction = async () => {
    setKycActioning(true);
    try {
      await customersApi.kycAction(customerId, kycActionType, kycMessage);
      toast.success(kycActionType === 'approve' ? 'KYC Approved!' : 'Changes requested — customer notified via WhatsApp');
      setKycActionType(null);
      setKycMessage('');
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setKycActioning(false); }
  };

  const handleAddPhone = async () => {
    if (!newPhone) return;
    try {
      await customersApi.addPhone(customerId, newPhone, newPhoneLabel, {
        name: newPhoneName,
        designation: newPhoneDesignation,
        email: newPhoneEmail,
        applies_to_linked: newPhoneShared,
      });
      toast.success('Contact added');
      setPhoneDialog(false);
      setNewPhone(''); setNewPhoneLabel(''); setNewPhoneName(''); setNewPhoneDesignation('other'); setNewPhoneEmail(''); setNewPhoneShared(false);
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const handleRemovePhone = async (phone) => {
    try { await customersApi.removePhone(customerId, phone); toast.success('Removed'); fetchCustomer(); } catch {}
  };

  const handleSavePhone = async () => {
    if (!editPhoneDialog) return;
    try {
      await customersApi.updatePhone(customerId, editPhoneDialog.oldPhone, {
        phone: editPhoneDialog.phone,
        label: editPhoneDialog.label,
        name: editPhoneDialog.name,
        designation: editPhoneDialog.designation,
        email: editPhoneDialog.email,
        applies_to_linked: !!editPhoneDialog.applies_to_linked,
      });
      toast.success('Contact updated');
      setEditPhoneDialog(null);
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openProfileDialog = () => {
    setProfileForm({
      company_name: cust.company_name || '',
      mobile: cust.mobile || '',
      email: cust.email || '',
      contact_person_name: cust.contact_person_name || '',
      contact_designation: cust.contact_designation || 'operations',
    });
    setProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await customersApi.update(customerId, profileForm);
      toast.success('Profile updated');
      setProfileDialog(false);
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSavingProfile(false); }
  };

  const openKycEdit = (step) => {
    const existing = (cust.kyc || {})[`step_${step}`] || {};
    // For step 2 default shipping_same_as_billing to true unless explicitly false
    let form = { ...existing };
    if (step === 2 && form.shipping_same_as_billing === undefined) {
      form.shipping_same_as_billing = !(form.shipping_address_1 || form.shipping_city);
    }
    setKycEditDialog({ step, form });
  };

  const handleSaveKycEdit = async () => {
    if (!kycEditDialog) return;
    setSavingKycEdit(true);
    try {
      // If shipping_same_as_billing is true, drop shipping_* fields so they're not stored
      let data = { ...kycEditDialog.form };
      if (kycEditDialog.step === 2 && data.shipping_same_as_billing) {
        ['shipping_address_1', 'shipping_address_2', 'shipping_city', 'shipping_state', 'shipping_pin'].forEach(k => { data[k] = ''; });
      }
      await customersApi.updateKyc(customerId, kycEditDialog.step, data);
      toast.success(`${STEP_TITLE[kycEditDialog.step]} saved`);
      setKycEditDialog(null);
      fetchCustomer();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSavingKycEdit(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await customersApi.delete(customerId); toast.success('Customer deleted'); navigate('/customers'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setDeleting(false); }
  };

  const handleReply = async (queryId) => {
    const msg = replyTexts[queryId]?.trim();
    if (!msg) return;
    try {
      await customersApi.replyQuery(queryId, msg);
      toast.success('Reply sent');
      setReplyTexts(p => ({ ...p, [queryId]: '' }));
      fetchCustomer();
    } catch { toast.error('Failed'); }
  };

  const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>;
  if (!cust) return null;

  const kyc = cust.kyc || {};
  const st = cust.kyc_status || 'pending';
  const kycPct = Math.min(100, ((cust.kyc_step || 0) / 5) * 100);
  const canApprove = st === 'complete' || st === 'in_progress' || st === 'changes_requested';

  const Field = ({ label, value }) => value ? (
    <div className="flex justify-between gap-3 py-2 border-b border-dashed last:border-0">
      <span className="text-xs sm:text-sm text-muted-foreground flex-shrink-0">{label}</span>
      <span className="text-xs sm:text-sm font-medium text-right break-words min-w-0">{value}</span>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="customer-detail-page">
      <Header
        title={cust.company_name || 'Customer'}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/customers')} data-testid="back-btn" className="h-9">
            <ArrowLeft className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">All Customers</span>
          </Button>
        }
      />

      <div className="max-w-screen-2xl mx-auto px-3 md:px-8 py-4 md:py-6 space-y-5 md:space-y-6">
        {/* Hero: identity + KYC status + actions */}
        <div className="rounded-2xl bg-gradient-to-br from-white to-amber-50/40 dark:from-neutral-900 dark:to-amber-500/5 border border-slate-200/70 dark:border-neutral-800 shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-2xl md:text-3xl font-bold text-amber-700 dark:text-amber-400 flex-shrink-0">
                {(cust.company_name || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-lg md:text-2xl font-bold tracking-tight truncate">{cust.company_name || 'Customer'}</p>
                <p className="text-xs md:text-sm text-muted-foreground mt-0.5 truncate">
                  <span className="font-mono">{cust.mobile || '—'}</span>
                  {cust.email && <> <span className="opacity-40 mx-1">·</span> {cust.email}</>}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className={cn("text-[10px] px-2 py-0.5", KYC_COLORS[st])}>{KYC_LABELS[st]}</Badge>
                  <span className="text-[11px] text-muted-foreground">KYC {cust.kyc_step || 0}/5</span>
                  <Progress value={kycPct} className="h-1.5 w-24 sm:w-32" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap md:flex-nowrap md:justify-end">
              <Button size="sm" variant="outline" className="h-9 flex-1 md:flex-initial" onClick={openProfileDialog} data-testid="edit-profile-btn">
                <Pencil className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Edit Profile</span><span className="sm:hidden">Edit</span>
              </Button>
              {canApprove && (
                <>
                  <Button size="sm" className="h-9 flex-1 md:flex-initial bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setKycActionType('approve')} data-testid="approve-kyc-btn">
                    <CheckCircle2 className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Approve</span><span className="sm:hidden">OK</span>
                  </Button>
                  <Button size="sm" variant="outline" className="h-9 flex-1 md:flex-initial text-red-600 border-red-200 hover:bg-red-50" onClick={() => setKycActionType('request_changes')} data-testid="request-changes-btn">
                    <XCircle className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Request Changes</span><span className="sm:hidden">Changes</span>
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" className="h-9 w-9 md:w-auto md:flex-initial text-red-500 border-red-200 hover:bg-red-50 p-0 md:px-3" onClick={() => setDeleteConfirm(true)} data-testid="delete-customer-btn" aria-label="Delete customer">
                <Trash2 className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Delete</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: KYC Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* KYC Step progress */}
            <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 pb-1 bg-white dark:bg-neutral-900 rounded-xl border border-slate-200/70 dark:border-neutral-800 p-3">
              {STEP_META.map((s, i) => {
                const done = !!kyc[`step_${s.num}`];
                return (
                  <React.Fragment key={s.num}>
                    <div className={cn("flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap",
                      done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400" : "bg-muted text-muted-foreground"
                    )}>
                      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                      <span className="hidden sm:inline">{s.label}</span>
                      <span className="sm:hidden">{s.num}</span>
                    </div>
                    {i < 4 && <div className={cn("h-0.5 w-3 rounded flex-shrink-0", done ? "bg-emerald-400" : "bg-border")} />}
                  </React.Fragment>
                );
              })}
            </div>

          {/* Step 1: Company */}
          <KYCCard title="Company Details" icon={Building2} data={kyc.step_1} onEdit={() => openKycEdit(1)}>
            <Field label="Company Name" value={kyc.step_1?.company_name} />
            <Field label="Legal Name" value={kyc.step_1?.legal_name} />
            <Field label="Organization Type" value={kyc.step_1?.org_type} />
            <Field label="Mobile" value={kyc.step_1?.mobile} />
            <Field label="Email" value={kyc.step_1?.email} />
          </KYCCard>

          {/* Step 2: Address */}
          <KYCCard title="Addresses" icon={MapPin} data={kyc.step_2} onEdit={() => openKycEdit(2)}>
            <Field label="Billing Address" value={[kyc.step_2?.billing_address_1, kyc.step_2?.billing_address_2].filter(Boolean).join(', ')} />
            <Field label="Billing City" value={kyc.step_2?.billing_city} />
            <Field label="Billing State" value={kyc.step_2?.billing_state} />
            <Field label="PIN Code" value={kyc.step_2?.billing_pin} />
            {kyc.step_2 && !kyc.step_2.shipping_same_as_billing && (
              <>
                <Separator className="my-2" />
                <Field label="Shipping Address" value={[kyc.step_2?.shipping_address_1, kyc.step_2?.shipping_address_2].filter(Boolean).join(', ')} />
                <Field label="Shipping City" value={kyc.step_2?.shipping_city} />
                <Field label="Shipping State" value={kyc.step_2?.shipping_state} />
                <Field label="Shipping PIN" value={kyc.step_2?.shipping_pin} />
              </>
            )}
          </KYCCard>

          {/* Step 3: Documents */}
          <KYCCard title="Documents" icon={FileText} data={kyc.step_3} onEdit={() => openKycEdit(3)}>
            <Field label="GST Number" value={kyc.step_3?.gst_number} />
            <Field label="PAN Number" value={kyc.step_3?.pan_number} />
            <Field label="MSME Registration" value={kyc.step_3?.msme_number} />
          </KYCCard>

          {/* Step 4: Banking */}
          <KYCCard title="Banking Details" icon={Landmark} data={kyc.step_4} onEdit={() => openKycEdit(4)}>
            <Field label="Account Holder" value={kyc.step_4?.account_holder} />
            <Field label="Account Number" value={kyc.step_4?.account_number} />
            <Field label="Account Type" value={kyc.step_4?.account_type} />
            <Field label="IFSC Code" value={kyc.step_4?.ifsc} />
            <Field label="Bank Name" value={kyc.step_4?.bank_name} />
            <Field label="Branch" value={kyc.step_4?.branch_name} />
            <Field label="Branch Address" value={kyc.step_4?.branch_address} />
          </KYCCard>

          {/* Step 5: Contacts (merged with Contact Persons panel) */}
          <KYCCard title="Contact Persons" icon={Users} data={(cust.additional_phones && cust.additional_phones.length > 0) || cust.contact_person_name ? { ok: true } : null}>
            <p className="text-xs text-muted-foreground mb-2">Managed in the <strong>Contact Persons</strong> panel on the right. Customer cannot edit the primary login contact.</p>
            <div className="space-y-2">
              {cust.contact_person_name && (
                <div className="flex items-center justify-between gap-2 text-sm py-1">
                  <span className="font-medium truncate">{cust.contact_person_name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{cust.contact_designation || 'operations'}</span>
                </div>
              )}
              {(cust.additional_phones || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm py-1">
                  <span className="truncate">{p.name || <span className="text-muted-foreground italic">No name</span>}</span>
                  <span className="text-xs text-muted-foreground capitalize">{p.designation || 'other'}</span>
                </div>
              ))}
            </div>
          </KYCCard>
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* Receivables snapshot — quick numbers, click for full panel below */}
          <ReceivablesMiniCard customerId={customerId} />

          {/* Linked Companies (Sister firms) */}
          <Card>
            <CardHeader className="pb-3 px-4 pt-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Link2 className="h-4 w-4" /> Linked Companies
                </CardTitle>
                <Button size="sm" variant="ghost" className="h-9 px-2.5 text-xs" onClick={openLinkDialog} data-testid="link-company-btn">
                  <Plus className="h-4 w-4 mr-1" />Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {linkedCompanies.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No linked companies. Link if same owner runs multiple firms — contacts can then be shared.</p>
              ) : (
                linkedCompanies.map(lc => (
                  <div key={lc.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-purple-50/60 dark:bg-purple-500/10 border border-purple-200/60">
                    <Building2 className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    <button
                      onClick={() => navigate(`/customers/${lc.id}`)}
                      className="flex-1 min-w-0 text-left hover:underline"
                      data-testid={`linked-company-${lc.id}`}
                    >
                      <p className="text-sm font-medium truncate">{lc.company_name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{(lc.kyc_status || 'pending').replace('_',' ')} · KYC {lc.kyc_step || 0}/5</p>
                    </button>
                    <button
                      onClick={() => handleUnlink(lc.id)}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-purple-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 flex-shrink-0"
                      aria-label="Unlink"
                      data-testid={`unlink-${lc.id}`}
                    >
                      <Unlink className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Contact Persons (merged: primary login contact + additional contacts) */}
          <Card>
            <CardHeader className="pb-3 px-4 pt-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Contact Persons</CardTitle>
                <Button size="sm" variant="ghost" className="h-9 px-2.5 text-xs" onClick={() => setPhoneDialog(true)} data-testid="add-phone-btn">
                  <Plus className="h-4 w-4 mr-1" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {/* Primary login contact — locked, edited via Edit Profile */}
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 space-y-1.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold">{cust.contact_person_name || <span className="text-muted-foreground italic">No name set</span>}</p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Badge className="text-[10px] h-5 bg-amber-600 hover:bg-amber-600 text-white">Primary · Login</Badge>
                    <button onClick={openProfileDialog} className="h-7 w-7 rounded-md flex items-center justify-center text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-500/20" aria-label="Edit primary contact" data-testid="edit-primary-contact-btn">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="font-mono truncate">{cust.mobile}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="capitalize">{cust.contact_designation || 'operations'}</span>
                  {cust.email && <><span className="text-muted-foreground/40">·</span><span className="truncate">{cust.email}</span></>}
                </div>
                <div className="pt-1.5 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-[10px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Customer cannot edit this — Edit Profile to update.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={() => handleSendLoginLink(cust.mobile)}
                    disabled={sendingLinkPhone === cust.mobile}
                    data-testid={`send-link-${cust.mobile}`}
                  >
                    {sendingLinkPhone === cust.mobile ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                    Send login link
                  </Button>
                </div>
              </div>

              {/* Additional contacts */}
              {(cust.additional_phones || []).map(p => (
                <div key={p.phone} className="p-3 rounded-lg bg-muted/40 border space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name || <span className="text-muted-foreground italic">No name</span>}</p>
                      {p.applies_to_linked && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300 flex-shrink-0 flex items-center gap-0.5" title="Shared across linked companies">
                          <Link2 className="h-2.5 w-2.5" /> shared
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={() => setEditPhoneDialog({ oldPhone: p.phone, phone: p.phone, label: p.label || '', name: p.name || '', designation: p.designation || 'other', email: p.email || '', applies_to_linked: !!p.applies_to_linked })} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Edit" data-testid={`edit-phone-${p.phone}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleRemovePhone(p.phone)} className="h-7 w-7 rounded-md flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" aria-label="Remove">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="font-mono truncate">{p.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    <span className="capitalize">{p.designation || 'other'}</span>
                    {p.email && <><span className="text-muted-foreground/40">·</span><span className="truncate">{p.email}</span></>}
                    {p.label && <><span className="text-muted-foreground/40">·</span><span className="italic">{p.label}</span></>}
                  </div>
                  <div className="pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => handleSendLoginLink(p.phone)}
                      disabled={sendingLinkPhone === p.phone}
                      data-testid={`send-link-${p.phone}`}
                    >
                      {sendingLinkPhone === p.phone ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                      Send login link
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Info */}
          <Card>
            <CardContent className="p-4 space-y-1">
              <Field label="Joined" value={fmtDate(cust.created_at)} />
              <Field label="Last Updated" value={fmtDate(cust.updated_at)} />
              {cust.invited_by && <Field label="Invited By" value={cust.invited_by} />}
              {cust.kyc_approved_by && <Field label="Approved By" value={cust.kyc_approved_by} />}
              {cust.contact_person_name && <Field label="Contact Person" value={cust.contact_person_name} />}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Secondary row: Queries (wider) + Documents (uploads) — full-width, balanced */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Queries */}
        <Card>
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4" /> Queries ({(cust.queries || []).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            {(cust.queries || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No queries yet</p>
            ) : (cust.queries || []).map(q => (
              <div key={q.id} className="p-3 rounded-lg border space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px] h-5">{q.category}</Badge>
                  <Badge variant={q.status === 'open' ? 'destructive' : 'default'} className="text-[10px] h-5">{q.status}</Badge>
                </div>
                <p className="text-sm font-semibold">{q.subject}</p>
                <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                  {q.messages?.map((m, i) => (
                    <div key={i} className={cn("p-2 rounded text-xs leading-relaxed", m.sender === 'customer' ? "bg-amber-50 dark:bg-amber-500/10" : "bg-blue-50 dark:bg-blue-500/10")}>
                      <span className="font-semibold">{m.sender_name}:</span> {m.message}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={replyTexts[q.id] || ''} onChange={e => setReplyTexts(p => ({ ...p, [q.id]: e.target.value }))}
                    placeholder="Reply..." className="text-sm h-9" onKeyDown={e => { if (e.key === 'Enter') handleReply(q.id); }} />
                  <Button size="icon" className="h-9 w-9 flex-shrink-0 bg-amber-600 hover:bg-amber-700" onClick={() => handleReply(q.id)}>
                    <Send className="h-4 w-4 text-white" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Documents (file uploads) */}
        <Card>
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <FileText className="h-4 w-4" /> Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <DocumentSection
              title="Pricing & Terms Chart"
              category="pricing_terms"
              docs={documents.filter(d => d.category === 'pricing_terms')}
              fileRef={docFileRefs.pricing_terms}
              uploading={uploadingCategory === 'pricing_terms'}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
            />
            <DocumentSection
              title="MSME Certificate"
              category="msme_certificate"
              docs={documents.filter(d => d.category === 'msme_certificate')}
              fileRef={docFileRefs.msme_certificate}
              uploading={uploadingCategory === 'msme_certificate'}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
            />
            <DocumentSection
              title="GST Certificate"
              category="gst_certificate"
              docs={documents.filter(d => d.category === 'gst_certificate')}
              fileRef={docFileRefs.gst_certificate}
              uploading={uploadingCategory === 'gst_certificate'}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
            />
            <DocumentSection
              title="Other"
              category="other"
              docs={documents.filter(d => d.category === 'other')}
              fileRef={docFileRefs.other}
              uploading={uploadingCategory === 'other'}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
              showLabelInput
              labelValue={otherLabel}
              onLabelChange={setOtherLabel}
            />
          </CardContent>
        </Card>
      </div>

      {/* Below the profile — tabbed workspace to eliminate long scrolling */}
      <div id="customer-detail-tabs" className="scroll-mt-20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="sticky top-14 z-30 -mx-4 md:mx-0 px-4 md:px-0 py-2 bg-slate-50/95 dark:bg-neutral-950/95 backdrop-blur border-b border-slate-200/70 dark:border-neutral-800">
            <TabsList className="grid grid-cols-4 w-full max-w-2xl h-auto" data-testid="customer-detail-tabs">
              <TabsTrigger value="orders" className="py-2 text-xs md:text-sm" data-testid="tab-orders">
                <FileText className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Orders
              </TabsTrigger>
              <TabsTrigger value="activity" className="py-2 text-xs md:text-sm" data-testid="tab-activity">
                <MessageSquare className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Activity
              </TabsTrigger>
              <TabsTrigger value="receivables" className="py-2 text-xs md:text-sm" data-testid="tab-receivables">
                <Wallet className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Receivables
              </TabsTrigger>
              <TabsTrigger value="ledger" className="py-2 text-xs md:text-sm" data-testid="tab-ledger">
                <IndianRupee className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" /> Ledger
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="orders" className="mt-0 space-y-5" data-testid="tab-content-orders">
            <ActiveOrdersCard customerId={customerId} />
          </TabsContent>

          <TabsContent value="activity" className="mt-0 space-y-5" data-testid="tab-content-activity">
            <CustomerActivityLog customerId={customerId} />
          </TabsContent>

          <TabsContent value="receivables" className="mt-0 space-y-5" data-testid="tab-content-receivables">
            <div id="receivables-section" className="scroll-mt-20">
              <CustomerReceivablesPanel customerId={customerId} />
            </div>
          </TabsContent>

          <TabsContent value="ledger" className="mt-0 space-y-5" data-testid="tab-content-ledger">
            <CustomerAdjustments customerId={customerId} />
          </TabsContent>
        </Tabs>
      </div>
      </div>

      {/* KYC Action Dialog */}
      <Dialog open={!!kycActionType} onOpenChange={() => setKycActionType(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{kycActionType === 'approve' ? 'Approve KYC' : 'Request Changes'}</DialogTitle>
            <DialogDescription>{kycActionType === 'approve' ? 'Customer will be notified via WhatsApp' : 'Describe what needs to change — customer will be notified'}</DialogDescription>
          </DialogHeader>
          {kycActionType === 'request_changes' && (
            <Textarea value={kycMessage} onChange={e => setKycMessage(e.target.value)} placeholder="e.g., Please upload GST certificate and verify PAN number" rows={4} className="text-sm" data-testid="kyc-change-message" />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setKycActionType(null)}>Cancel</Button>
            <Button onClick={handleKycAction} disabled={kycActioning || (kycActionType === 'request_changes' && !kycMessage.trim())}
              className={kycActionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-kyc-action">
              {kycActioning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {kycActionType === 'approve' ? 'Approve' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Phone Dialog */}
      <Dialog open={phoneDialog} onOpenChange={setPhoneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Contact Person</DialogTitle>
            <DialogDescription>Add another stakeholder for this customer (e.g., Accounts team).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm">Name *</Label><Input value={newPhoneName} onChange={e => setNewPhoneName(e.target.value)} className="h-11 text-sm" data-testid="add-phone-name" /></div>
              <div className="space-y-1.5">
                <Label className="text-sm">Designation</Label>
                <Select value={newPhoneDesignation} onValueChange={setNewPhoneDesignation}>
                  <SelectTrigger className="h-11 text-sm" data-testid="add-phone-designation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="accounts">Accounts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">WhatsApp *</Label><Input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="919876543210" className="h-11 text-sm font-mono" data-testid="add-phone-input" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Email</Label><Input type="email" value={newPhoneEmail} onChange={e => setNewPhoneEmail(e.target.value)} className="h-11 text-sm" data-testid="add-phone-email" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Label (optional)</Label><Input value={newPhoneLabel} onChange={e => setNewPhoneLabel(e.target.value)} placeholder="Office, Personal, Owner..." className="h-11 text-sm" /></div>
            {(cust.linked_customer_ids || []).length > 0 && (
              <label className="flex items-center gap-2.5 cursor-pointer pt-1 select-none">
                <input type="checkbox" checked={newPhoneShared} onChange={e => setNewPhoneShared(e.target.checked)} className="h-4 w-4 accent-purple-600" data-testid="add-phone-shared" />
                <span className="text-sm">Apply to linked sister companies <span className="text-xs text-muted-foreground">({(cust.linked_customer_ids || []).length} linked)</span></span>
              </label>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPhoneDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPhone} disabled={!newPhone || !newPhoneName} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-add-phone-btn"><Plus className="h-4 w-4 mr-1" />Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>This will permanently delete {cust.company_name} and all their queries. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Profile Dialog */}
      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Core customer details. KYC sections are edited from their own cards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Company Name</Label>
              <Input value={profileForm.company_name} onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))} className="h-11 text-sm" data-testid="profile-company" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm">Contact Person</Label>
                <Input value={profileForm.contact_person_name} onChange={e => setProfileForm(p => ({ ...p, contact_person_name: e.target.value }))} className="h-11 text-sm" data-testid="profile-contact" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Designation</Label>
                <Select value={profileForm.contact_designation} onValueChange={v => setProfileForm(p => ({ ...p, contact_designation: v }))}>
                  <SelectTrigger className="h-11 text-sm" data-testid="profile-designation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="accounts">Accounts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Primary Mobile (WhatsApp)</Label>
              <Input value={profileForm.mobile} onChange={e => setProfileForm(p => ({ ...p, mobile: e.target.value }))} placeholder="919876543210" className="h-11 text-sm font-mono" data-testid="profile-mobile" />
              <p className="text-xs text-muted-foreground">Login number. Customer cannot change this.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Email</Label>
              <Input type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} className="h-11 text-sm" data-testid="profile-email" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setProfileDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Company Dialog */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Link a sister company</DialogTitle>
            <DialogDescription>
              Pick another customer that shares the same owner. They'll see each other in their portals and shared contacts will sync.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 flex-1 overflow-hidden flex flex-col">
            <Input
              placeholder="Search by company name, mobile..."
              value={linkSearch}
              onChange={e => setLinkSearch(e.target.value)}
              className="h-10"
              data-testid="link-search"
            />
            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5">
              {allCustomers
                .filter(c => c.id !== customerId)
                .filter(c => !(cust.linked_customer_ids || []).includes(c.id))
                .filter(c => {
                  if (!linkSearch) return true;
                  const q = linkSearch.toLowerCase();
                  return (c.company_name || '').toLowerCase().includes(q) || (c.mobile || '').includes(q);
                })
                .slice(0, 50)
                .map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleLink(c.id)}
                    disabled={linking === c.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/40 transition-colors text-left disabled:opacity-50"
                    data-testid={`link-target-${c.id}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-sm font-bold text-amber-700 dark:text-amber-400 flex-shrink-0">
                      {(c.company_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{c.company_name}</p>
                      <p className="text-xs text-muted-foreground">{c.mobile}</p>
                    </div>
                    {linking === c.id ? <Loader2 className="h-4 w-4 animate-spin text-purple-600" /> : <Link2 className="h-4 w-4 text-purple-500" />}
                  </button>
                ))}
              {allCustomers.filter(c => c.id !== customerId && !(cust.linked_customer_ids || []).includes(c.id)).length === 0 && (
                <p className="text-sm text-muted-foreground italic py-4 text-center">No other customers available to link.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Phone Dialog */}
      <Dialog open={!!editPhoneDialog} onOpenChange={(o) => { if (!o) setEditPhoneDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Contact Person</DialogTitle>
          </DialogHeader>
          {editPhoneDialog && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-sm">Name</Label><Input value={editPhoneDialog.name || ''} onChange={e => setEditPhoneDialog(p => ({ ...p, name: e.target.value }))} className="h-11 text-sm" data-testid="edit-phone-name" /></div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Designation</Label>
                  <Select value={editPhoneDialog.designation || 'other'} onValueChange={v => setEditPhoneDialog(p => ({ ...p, designation: v }))}>
                    <SelectTrigger className="h-11 text-sm" data-testid="edit-phone-designation"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-sm">WhatsApp *</Label><Input value={editPhoneDialog.phone} onChange={e => setEditPhoneDialog(p => ({ ...p, phone: e.target.value }))} className="h-11 text-sm font-mono" data-testid="edit-phone-input" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Email</Label><Input type="email" value={editPhoneDialog.email || ''} onChange={e => setEditPhoneDialog(p => ({ ...p, email: e.target.value }))} className="h-11 text-sm" data-testid="edit-phone-email" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Label (optional)</Label><Input value={editPhoneDialog.label} onChange={e => setEditPhoneDialog(p => ({ ...p, label: e.target.value }))} placeholder="Office, Personal..." className="h-11 text-sm" data-testid="edit-phone-label" /></div>
              {(cust.linked_customer_ids || []).length > 0 && (
                <label className="flex items-center gap-2.5 cursor-pointer pt-1 select-none">
                  <input type="checkbox" checked={!!editPhoneDialog.applies_to_linked} onChange={e => setEditPhoneDialog(p => ({ ...p, applies_to_linked: e.target.checked }))} className="h-4 w-4 accent-purple-600" data-testid="edit-phone-shared" />
                  <span className="text-sm">Apply to linked sister companies <span className="text-xs text-muted-foreground">({(cust.linked_customer_ids || []).length} linked)</span></span>
                </label>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditPhoneDialog(null)}>Cancel</Button>
            <Button onClick={handleSavePhone} disabled={!editPhoneDialog?.phone} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-phone-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit KYC Step Dialog */}
      <Dialog open={!!kycEditDialog} onOpenChange={(o) => { if (!o) setKycEditDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit · {kycEditDialog && STEP_TITLE[kycEditDialog.step]}</DialogTitle>
            <DialogDescription>
              Admin override. Empty fields will be cleared. Approved KYC stays approved on save.
            </DialogDescription>
          </DialogHeader>
          {kycEditDialog && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
              <KycEditFields
                step={kycEditDialog.step}
                form={kycEditDialog.form}
                onChange={(key, val) => setKycEditDialog(p => ({ ...p, form: { ...p.form, [key]: val } }))}
              />
            </div>
          )}
          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setKycEditDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveKycEdit} disabled={savingKycEdit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-kyc-edit-btn">
              {savingKycEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Renders the right input for each KYC step field.
const KycEditFields = ({ step, form, onChange }) => {
  const fields = (KYC_STEP_FIELDS[step] || []).filter(f => !f.hideIf || !f.hideIf(form));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {fields.map(f => {
        const wrapClass = cn("space-y-1.5", (f.span === 2) && "sm:col-span-2");
        if (f.type === 'checkbox') {
          return (
            <label key={f.key} className={cn("flex items-center gap-2.5 cursor-pointer pt-1", wrapClass)}>
              <input
                type="checkbox"
                checked={!!form[f.key]}
                onChange={e => onChange(f.key, e.target.checked)}
                className="h-4 w-4 accent-amber-600"
                data-testid={`kyc-edit-${f.key}`}
              />
              <span className="text-sm font-medium">{f.label}</span>
            </label>
          );
        }
        if (f.type === 'select') {
          return (
            <div key={f.key} className={wrapClass}>
              <Label className="text-sm">{f.label}</Label>
              <Select value={form[f.key] || ''} onValueChange={v => onChange(f.key, v)}>
                <SelectTrigger className="h-10 text-sm" data-testid={`kyc-edit-${f.key}`}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {(f.options || []).map(o => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          );
        }
        return (
          <div key={f.key} className={wrapClass}>
            <Label className="text-sm">{f.label}</Label>
            <Input
              value={form[f.key] || ''}
              onChange={e => onChange(f.key, f.upper ? e.target.value.toUpperCase() : e.target.value)}
              type={f.type === 'email' ? 'email' : 'text'}
              placeholder={f.placeholder}
              className="h-10 text-sm"
              data-testid={`kyc-edit-${f.key}`}
            />
          </div>
        );
      })}
    </div>
  );
};

// KYC Section Card
const KYCCard = ({ title, icon: Icon, data, children, onEdit }) => (
  <Card className={cn("transition-all", data ? "border-emerald-200/50 dark:border-emerald-500/10" : "border-dashed opacity-60")}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0", data ? "bg-emerald-100 dark:bg-emerald-500/20" : "bg-muted")}>
          {data ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <Icon className="h-4 w-4 text-muted-foreground" />}
        </div>
        <p className="text-sm sm:text-base font-bold flex-1 min-w-0 truncate">{title}</p>
        <Badge variant="outline" className="text-[10px] h-5 flex-shrink-0">{data ? 'Complete' : 'Pending'}</Badge>
        {onEdit && (
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={onEdit} aria-label={`Edit ${title}`}>
            <Pencil className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>
      {data ? <div className="sm:ml-12">{children}</div> : <p className="text-sm text-muted-foreground sm:ml-12">Not submitted yet</p>}
    </CardContent>
  </Card>
);

// Documents — per-category section
const DocumentSection = ({ title, category, docs, fileRef, uploading, onUpload, onDelete, onDownload, showLabelInput, labelValue, onLabelChange }) => {
  const fmtSize = (b) => b > 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[11px]"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`upload-${category}-btn`}
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />} Add PDF
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={e => {
            const f = e.target.files?.[0];
            e.target.value = '';
            onUpload(category, f);
          }}
        />
      </div>
      {showLabelInput && (
        <Input
          value={labelValue}
          onChange={e => onLabelChange(e.target.value)}
          placeholder="Label (e.g., Quality Policy)"
          className="h-8 text-xs mb-1"
          data-testid={`other-label-input`}
        />
      )}
      {docs.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No file uploaded</p>
      ) : (
        <div className="space-y-1">
          {docs.map(d => (
            <div key={d.id} className="flex items-center gap-2 p-2 rounded border bg-muted/30">
              <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{d.label || d.filename}</p>
                <p className="text-[10px] text-muted-foreground">{fmtSize(d.size_bytes)} · {new Date(d.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              </div>
              <button
                onClick={() => onDownload(d)}
                className="h-7 w-7 rounded-md flex items-center justify-center text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-500/10 flex-shrink-0"
                aria-label="Download"
                data-testid={`download-doc-${d.id}`}
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onDelete(d.id)}
                className="h-7 w-7 rounded-md flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 flex-shrink-0"
                aria-label="Delete"
                data-testid={`delete-doc-${d.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────────
// Customer Receivables Panel — payment terms, credit, invoices, payment schedule
// ──────────────────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  overdue: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300',
  partial: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300',
  current: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300',
  paid:    'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
  disputed:'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/20 dark:text-purple-300',
};

function _fmtINR(n) {
  if (n == null || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const num = Math.round(Math.abs(n));
  let s = String(num);
  if (s.length <= 3) return `${sign}\u20B9${s}`;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const groups = [];
  while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
  if (rest) groups.unshift(rest);
  return `${sign}\u20B9${groups.join(',')},${last3}`;
}

function _fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso.slice(0, 10); }
}

// ──────────────────────────────────────────────────────────────────────────
// Receivables MINI card — shown in the right sidebar of CustomerDetail.
// Lightweight snapshot; "View all" switches to the Receivables tab below.
// ──────────────────────────────────────────────────────────────────────────
const ReceivablesMiniCard = ({ customerId }) => {
  const [, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    arApi.customerReceivables(customerId)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent — full panel will show the error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  const openReceivablesTab = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'receivables');
      return next;
    }, { replace: true });
    requestAnimationFrame(() => {
      const el = document.getElementById('customer-detail-tabs');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (loading) {
    return (
      <Card data-testid="receivables-mini-card">
        <CardContent className="p-4 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-3 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const outstanding = data.outstanding || 0;
  const overdue = data.overdue || 0;
  const openCount = (data.open_invoices || []).length;
  const utilPct = data.credit_limit > 0 ? Math.min(100, (outstanding / data.credit_limit) * 100) : null;
  const noActivity = openCount === 0 && outstanding === 0;

  return (
    <Card data-testid="receivables-mini-card" className="overflow-hidden border-violet-200/60 dark:border-violet-500/30">
      <CardHeader className="pb-3 px-4 pt-4 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10 border-b border-violet-100 dark:border-violet-500/20">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 flex items-center gap-1.5">
            <Wallet className="h-4 w-4" /> Receivables
          </CardTitle>
          <button
            type="button"
            onClick={openReceivablesTab}
            className="text-[11px] font-medium text-violet-600 dark:text-violet-300 hover:underline flex items-center gap-0.5"
            data-testid="receivables-mini-view-full"
          >
            View all <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {noActivity ? (
          <div className="text-center py-2">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">No open invoices — all settled.</p>
          </div>
        ) : (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Outstanding</p>
              <p className={cn("text-xl font-bold tabular-nums leading-tight", overdue > 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-900 dark:text-slate-100")}>
                {_fmtINR(outstanding)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {openCount} open invoice{openCount === 1 ? '' : 's'}
              </p>
            </div>
            {overdue > 0 && (
              <div className="p-2 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200/60 dark:border-rose-500/20">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 dark:text-rose-300">Overdue</span>
                </div>
                <p className="text-sm font-bold tabular-nums text-rose-700 dark:text-rose-300 mt-0.5">{_fmtINR(overdue)}</p>
              </div>
            )}
            {utilPct != null && (
              <div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                  <span>Credit utilisation</span>
                  <span className={cn('font-semibold tabular-nums', utilPct > 90 ? 'text-rose-600' : utilPct > 70 ? 'text-amber-600' : 'text-emerald-600')}>{utilPct.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn('h-full transition-all', utilPct > 90 ? 'bg-rose-500' : utilPct > 70 ? 'bg-amber-500' : 'bg-emerald-500')}
                    style={{ width: `${utilPct}%` }}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className="flex items-center justify-between gap-2 pt-2 border-t text-[11px]">
          <div>
            <p className="text-muted-foreground uppercase tracking-wider text-[9px] font-semibold">Terms</p>
            <p className="font-semibold">Net {data.payment_terms_days}d</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground uppercase tracking-wider text-[9px] font-semibold">Credit limit</p>
            <p className="font-semibold tabular-nums">{data.credit_limit > 0 ? _fmtINR(data.credit_limit) : 'No limit'}</p>
          </div>
          {data.trust_grade && (
            <div className="text-right">
              <p className="text-muted-foreground uppercase tracking-wider text-[9px] font-semibold">Trust</p>
              <p className={cn('font-bold', data.trust_grade === 'A' ? 'text-emerald-600' : data.trust_grade === 'D' ? 'text-rose-600' : 'text-slate-700 dark:text-slate-300')}>
                {data.trust_grade}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CustomerReceivablesPanel = ({ customerId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ credit_limit: '', payment_terms_days: '' });
  const [expanded, setExpanded] = useState({});
  const [showPaid, setShowPaid] = useState(false);
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  // Edit an already-recorded invoice payment. Delete is inside the dialog
  // (super_admin only, gated backend-side).
  const [editingPayment, setEditingPayment] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    arApi.customerReceivables(customerId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load receivables'))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setForm({
      credit_limit: data?.credit_limit ? String(data.credit_limit) : '',
      payment_terms_days: data?.payment_terms_days ? String(data.payment_terms_days) : '',
    });
    setEditing(true);
  };

  const saveTerms = async () => {
    setSaving(true);
    try {
      await arApi.updateCustomerCredit(customerId, {
        credit_limit: form.credit_limit === '' ? null : Number(form.credit_limit),
        payment_terms_days: form.payment_terms_days === '' ? null : Number(form.payment_terms_days),
      });
      toast.success('Payment terms updated');
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-5 space-y-2">
          {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const open = data.open_invoices || [];
  const paid = data.paid_invoices || [];
  const buckets = data.buckets || {};
  const utilPct = data.credit_limit > 0 ? Math.min(100, (data.outstanding / data.credit_limit) * 100) : null;

  return (
    <Card data-testid="customer-receivables-panel" className="overflow-hidden">
      <CardHeader className="pb-3 px-4 md:px-5 pt-4 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10 border-b">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Wallet className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              Receivables &amp; Payment Schedule
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">All invoices for this customer with payment status and aging</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkReminderOpen(true)}
              disabled={!data || (data.open_invoices || []).length === 0}
              data-testid="send-statement-reminder-btn"
              title="Send a WhatsApp statement listing all pending invoices"
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> Send statement
            </Button>
            <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-payment-terms-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit terms
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 md:p-5 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
          <KPI label="Payment terms" value={`Net ${data.payment_terms_days}d`} icon={Calendar} testid="kpi-payment-terms" />
          <KPI label="Credit limit" value={data.credit_limit > 0 ? _fmtINR(data.credit_limit) : 'No limit'} icon={Lock} testid="kpi-credit-limit" />
          <KPI label="Outstanding" value={_fmtINR(data.outstanding)} icon={IndianRupee}
               accent={data.outstanding > 0 ? 'text-rose-600 dark:text-rose-400' : ''} testid="kpi-outstanding" />
          <KPI label="Overdue" value={_fmtINR(data.overdue)} icon={AlertTriangle}
               accent={data.overdue > 0 ? 'text-rose-600 dark:text-rose-400' : ''} testid="kpi-overdue" />
          <KPI label="Trust grade" value={data.trust_grade || '—'} icon={CheckCircle2}
               accent={data.trust_grade === 'A' ? 'text-emerald-600' : data.trust_grade === 'D' ? 'text-rose-600' : ''}
               testid="kpi-trust" />
        </div>

        {utilPct != null && (
          <div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
              <span>Credit utilisation</span>
              <span className={cn('font-semibold tabular-nums', utilPct > 90 ? 'text-rose-600' : utilPct > 70 ? 'text-amber-600' : 'text-emerald-600')}>{utilPct.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn('h-full transition-all', utilPct > 90 ? 'bg-rose-500' : utilPct > 70 ? 'bg-amber-500' : 'bg-emerald-500')}
                style={{ width: `${utilPct}%` }}
              />
            </div>
          </div>
        )}

        {Object.values(buckets).some(v => v > 0) && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">Aging:</span>
            {Object.entries(buckets).map(([k, v]) => (
              v > 0 ? (
                <Badge key={k} variant="outline" className={cn('text-[10px]',
                  k === 'current' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' :
                  k === '120_plus' ? 'bg-rose-100 text-rose-800 dark:bg-rose-500/30 dark:text-rose-200' :
                  k === '91_120' ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300' :
                  'bg-amber-50 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                )}>
                  {k.replace('_', '-')}: {_fmtINR(v)}
                </Badge>
              ) : null
            ))}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Open invoices ({open.length})</h4>
            {paid.length > 0 && (
              <button
                type="button"
                className="text-xs text-violet-600 dark:text-violet-400 hover:underline font-medium"
                onClick={() => setShowPaid(s => !s)}
                data-testid="toggle-paid-btn"
              >
                {showPaid ? 'Hide' : 'Show'} paid history ({paid.length})
              </button>
            )}
          </div>

          {open.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No open invoices — all settled.</p>
          ) : (
            <div className="divide-y border rounded-lg overflow-hidden" data-testid="customer-invoices-list">
              {open.map(inv => (
                <InvoiceRow
                  key={`${inv.order_id}-${inv.dispatch_id}`}
                  inv={inv}
                  expanded={!!expanded[inv.dispatch_id]}
                  onToggle={() => setExpanded(e => ({ ...e, [inv.dispatch_id]: !e[inv.dispatch_id] }))}
                  onOpenOrder={() => navigate(`/orders/${inv.order_id}`)}
                  onEditPayment={(p) => setEditingPayment({ orderId: inv.order_id, dispatchId: inv.dispatch_id, invoiceNumber: inv.invoice_number, payment: p })}
                />
              ))}
            </div>
          )}
        </div>

        {showPaid && paid.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Recent paid invoices</h4>
            <div className="divide-y border rounded-lg overflow-hidden" data-testid="customer-paid-list">
              {paid.slice().reverse().map(inv => (
                <InvoiceRow
                  key={`${inv.order_id}-${inv.dispatch_id}`}
                  inv={inv}
                  expanded={!!expanded[inv.dispatch_id]}
                  onToggle={() => setExpanded(e => ({ ...e, [inv.dispatch_id]: !e[inv.dispatch_id] }))}
                  onOpenOrder={() => navigate(`/orders/${inv.order_id}`)}
                  onEditPayment={(p) => setEditingPayment({ orderId: inv.order_id, dispatchId: inv.dispatch_id, invoiceNumber: inv.invoice_number, payment: p })}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={editing} onOpenChange={(o) => !o && setEditing(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit payment terms</DialogTitle>
            <DialogDescription>Set the credit limit and standard net payment terms for this customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">Payment terms (days)</Label>
              <Input
                type="number" min="0" max="365"
                value={form.payment_terms_days}
                onChange={e => setForm({ ...form, payment_terms_days: e.target.value })}
                placeholder="e.g. 30"
                data-testid="edit-payment-terms-days"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Net days from invoice date.</p>
            </div>
            <div>
              <Label className="text-xs">Credit limit (\u20B9)</Label>
              <Input
                type="number" min="0"
                value={form.credit_limit}
                onChange={e => setForm({ ...form, credit_limit: e.target.value })}
                placeholder="0 = no limit"
                data-testid="edit-credit-limit"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Maximum outstanding balance. 0 disables the check.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={saveTerms} disabled={saving} data-testid="save-payment-terms" className="bg-violet-600 hover:bg-violet-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkReminderDialog
        open={bulkReminderOpen}
        customer={{ id: customerId, company_name: data?.company_name }}
        onClose={() => setBulkReminderOpen(false)}
        onSent={() => load()}
      />

      <EditPaymentDialog
        open={!!editingPayment}
        orderId={editingPayment?.orderId}
        dispatchId={editingPayment?.dispatchId}
        invoiceNumber={editingPayment?.invoiceNumber}
        payment={editingPayment?.payment}
        onClose={() => setEditingPayment(null)}
        onSaved={() => load()}
      />
    </Card>
  );
};

// ─── Receive Payment (FIFO) Dialog ──────────────────────────────────────────

const KPI = ({ label, value, icon: Icon, accent = '', testid }) => (
  <div className="p-2.5 rounded-lg bg-muted/40 border" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    <p className={cn('mt-0.5 text-sm md:text-base font-bold tabular-nums', accent)}>{value}</p>
  </div>
);

const InvoiceRow = ({ inv, expanded, onToggle, onOpenOrder, onEditPayment }) => {
  const status = inv.status || 'current';
  return (
    <div className="hover:bg-muted/30" data-testid={`customer-invoice-${inv.invoice_number || inv.dispatch_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate max-w-[180px]">{inv.invoice_number || '—'}</span>
            <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded border capitalize', STATUS_COLOR[status] || STATUS_COLOR.current)}>
              {status}
            </span>
            {inv.days_overdue > 0 && (
              <span className="text-[10px] font-semibold text-rose-600 dark:text-rose-400">{inv.days_overdue}d overdue</span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
            {inv.co_number} · Due {_fmtDate(inv.due_date)}
            {inv.salesperson && ` · ${inv.salesperson}`}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold tabular-nums text-sm">{_fmtINR(inv.balance)}</p>
          <p className="text-[10px] text-muted-foreground">balance</p>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 -mt-1 space-y-2.5 bg-muted/20">
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div>
              <span className="text-muted-foreground">Invoiced</span>
              <p className="font-semibold tabular-nums">{_fmtINR(inv.total_amount)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Paid</span>
              <p className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{_fmtINR(inv.paid_amount)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Invoice date</span>
              <p className="font-semibold">{_fmtDate(inv.invoice_date)}</p>
            </div>
          </div>

          {Array.isArray(inv.payments) && inv.payments.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Payments received</p>
              <div className="space-y-1">
                {inv.payments.map((p) => (
                  <div key={p.id || `${p.date}-${p.amount}`} className="flex items-center justify-between gap-2 text-[11px] px-2 py-1.5 rounded bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/40 dark:border-emerald-500/20 group">
                    <span className="min-w-0 flex-1 truncate">
                      <CheckCircle2 className="h-3 w-3 inline mr-1 text-emerald-600" />
                      {_fmtDate(p.date)} · {p.payment_mode || 'bank'}
                      {p.reference_number && ` · ${p.reference_number}`}
                    </span>
                    <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300 flex-shrink-0">{_fmtINR(p.amount)}</span>
                    {onEditPayment && p.id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEditPayment(p); }}
                        className="flex-shrink-0 opacity-40 hover:opacity-100 hover:text-violet-600 dark:hover:text-violet-400 transition-opacity"
                        title="Edit payment (super_admin can also delete)"
                        data-testid={`edit-payment-${p.id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button size="sm" variant="outline" className="w-full h-8 text-xs" onClick={onOpenOrder} data-testid={`open-order-${inv.dispatch_id}`}>
            Open order <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};


export default CustomerDetail;
